<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <BillingFeatureGate feature="supplier_access"
                            variant="page"
                            :title="$t('Supplier Logins')"
                            :description="$t('billing.features.supplier_access.tooltip')"
                            icon="ti-key">
          <Toolbar class="p-mb-4 p-p-2 page-toolbar">
            <template #start>
              <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Supplier Logins') }}</div>
              <div class="p-p-1" v-show="canModerate">
                <AddNewButton :loading="!allDataLoaded" :disabled="!allDataLoaded" :label="$t('Add')" @click="addNewItem"/>
              </div>
            </template>
            <template #end>
              <div class="p-p-1">
                <SearchInput :disabled="dataTableIsLoading"
                             @change-processed-search-value="changeProcessedSearchValue"
                             v-model="searchData"
                             @show-spinner="isLoading = true"/>
              </div>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :showHeader="false"
                            :dataTableIsLoading="dataTableIsLoading"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
              {name: 'name', title: $t('Name'), sortable: true},
              {name: 'favicon', width: '40px', sortable: false},
              {name: 'url', title: 'URL', sortable: true},
              {name: 'username', title: $t('Username'), sortable: false},
              {name: 'password', title: $t('Password'), sortable: false},
              {name: 'comment', title: $t('Comment'), sortable: false},
              {name: 'access', title: $t('Access'), sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
            <template v-slot:body>
              <tr v-for="item of items"
                  :key="item.id"
                  :class="{'updated-item': isItemUpdated(item.id)}">
                <td class="word-break">
                  <span v-if="item.name">{{ item.name }}</span>
                  <i v-else class="ti-minus"/>
                </td>

                <td>
                  <img v-if="item.url" :src="`https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(normalizeUrl(item.url))}&size=48`" style="height: 20px;width: 20px; display: block" alt="">
                  <i v-else class="ti-minus"/>
                </td>

                <td class="word-break">
                  <div v-if="item.url" style="width: 20vw; max-width: 1160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                    <a :href="normalizeUrl(item.url)"
                       target="_blank"
                       rel="noopener noreferrer"
                       draggable="false"
                       class="word-break">
                      {{ item.url }}
                    </a>
                  </div>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="word-break">
                  <span v-if="item.username">{{ item.username }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="word-break">
                  <span v-if="item.password && item.passwordLoaded && !item.passwordIsLoading">{{ item.password }}</span>
                  <i v-else-if="!item.password && item.passwordLoaded && !item.passwordIsLoading" class="ti-minus"></i>
                  <Button v-else @click.prevent="getPassword(item)"
                          class="p-button-sm p-p-1 p-button-text nowrap"
                          :icon="'ti-eye'"
                          :loading="item.passwordIsLoading"
                          :label="$t('Show password')"/>
                </td>
                <td class="word-break">
                  <div v-if="item.comment" style="width: 15vw; max-width: 700px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">{{ item.comment }}</div>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <div class="">
                    <div class="p-badge"
                         v-if="item.users?.length"
                         v-tooltip.top="createTooltipData(item)">{{ item.users.length }}
                    </div>
                    <div v-else class="p-badge">0</div>
                  </div>
                </td>
                <td class="table-date-line">
                  {{ formatBy(item.created_at, item.creator) }}
                </td>
                <td v-show="actionButtonsCount">
                  <div class="actions">
                    <EditButton :disabled="!allDataLoaded" @click="editItem(item)"/>
                    <DeleteButton @click="confirmItemDelete(item)"
                                  :disabled="dataTableIsLoading"/>
                  </div>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="items && items.length"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

          <SupplierAccountModal :visible="itemModal"
                                :item="item"
                                :linkUsers="employees"
                                @update-items="updateItems"
                                @close="closeItemModal"/>

          <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                              :modalDataIsLoading="modalDataIsLoading"
                              @close="closeConfirmItemDeleteModal"
                              @delete-item="deleteItem"
                              :data="itemToDeleteData"/>
        </BillingFeatureGate>
      </div>

    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import httpMixins from "@/mixins/httpMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import SupplierAccountModal from "@/pages/settings/components/SupplierAccountModal.vue";
import BillingFeatureGate from "@/components/billing/BillingFeatureGate.vue";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'

export default {
  mixins: [ datatableMixins, formatMixins, showErrorsMixins, httpMixins, billingFeaturesMixins ],
  components: { SupplierAccountModal, BillingFeatureGate },
  data() {
    return {
      employees: null,
      fieldsExpand: 'users,users.userData,creator',
      apiLink: 'link',
      deleteItemUrl: 'link/delete',
      routePath: '/supplier-accounts-settings',
      sort: {
        column: 'id',
        direction: '-',
      },
    }
  },
  watch: {
    canUseSupplierAccess(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetSupplierAccountsSettings()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    if (this.canUseSupplierAccess) {
      this.checkRouteQuery()
      this.getPageData()
    }
  },
  methods: {
    getPageData() {
      if (!this.canUseSupplierAccess || !this.userCanGetData || this.fetchInFlight) return
      this.getItems()
      this.getModerators()
    },
    resetSupplierAccountsSettings() {
      this._abortors?.supplierAccountsSettings?.abort?.()
      this.items = []
      this.employees = null
      this.item = {}
      this.itemModal = false
      this.confirmItemDeleteModal = false
      this.pagination.currentPage = 1
      this.pagination.pageCount = 1
      this.pagination.totalCount = 0
      this.isLoading = false
      this.dataTableIsLoading = false
    },
    encodeURIComponent(value) {
      return encodeURIComponent(value)
    },
    async getPassword(access) {
      if (!this.canUseSupplierAccess || !access?.id) return
      access.passwordIsLoading = true

      try {
        const { data, status } = await httpClient.post('link/get-password', { id: access.id })
        if (status === 200 && data?.success) {
          access.passwordLoaded = true
          access.password = data.password
        } else {
          return null
        }
      } catch(err) {
        if (!this.canUseSupplierAccess) return
        this.showError(err)
      } finally {
        access.passwordIsLoading = false
      }
    },
    createTooltipData(item) {
      if (!item.users?.length) {
        return false
      }

      const users = item.users
      let html = ''
      users.forEach(user => {
        html += `<div>${user.userData?.first_name || ''} ${user.userData?.last_name || ''}</div>`
      })
      return html
    },
    async getItems() {
      if (!this.canUseSupplierAccess || !this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('supplierAccountsSettings')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data.map(item => ({
            ...item,
            password: null,
            passwordLoaded: false,
            passwordIsLoading: false,
          }))

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        if (!this.canUseSupplierAccess) return
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.supplierAccountsSettings === ctrl) {
          this._abortors.supplierAccountsSettings = null
        }
      }
    },
  },
  computed: {
    allDataLoaded() {
      return !!(this.employees)
    },

    canModerate() {
      return this.$store.getters.isAdminOrHigher
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    itemToDeleteData() {
      if (!this.itemToDelete?.name) return null
      return this.itemToDelete.name
    },
  }
}
</script>
